import {
  ApolloClient,
  InMemoryCache,
  createHttpLink,
  gql,
} from "@apollo/client";

// AI Automation GraphQL endpoint (FIPS AI automation API)
const AI_AUTOMATION_GRAPHQL_ENDPOINT =
  "https://yuxwem7vg5hinnbj4xpibeeysi.appsync-api.us-east-1.amazonaws.com/graphql";

// Create HTTP link with proper headers for AppSync
const httpLink = createHttpLink({
  uri: AI_AUTOMATION_GRAPHQL_ENDPOINT,
  headers: {
    "x-api-key": "da2-kiurin6m3fh5lcqhonvvvrgdsi", // Replace with actual API key
    "Content-Type": "application/json",
  },
});

// Initialize Apollo Client for AI automation services
const aiAutomationClient = new ApolloClient({
  link: httpLink,
  cache: new InMemoryCache(),
  defaultOptions: {
    watchQuery: {
      errorPolicy: "all",
    },
    query: {
      errorPolicy: "all",
    },
    mutate: {
      errorPolicy: "all",
    },
  },
});

/**
 * Test function to debug API connection issues
 * Call this to verify the API configuration
 */
export const testAiAutomationApiConnection = async () => {
  try {
    console.log("Testing AI Automation API connection...");
    console.log("Endpoint:", AI_AUTOMATION_GRAPHQL_ENDPOINT);

    // Add a simple test query here when the schema is available
    // For now, just test the connection
    return {
      success: true,
      message: "AI Automation API connection test completed",
    };
  } catch (error) {
    console.error("AI Automation API connection test failed:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Create an AI automation workflow
 */
export const createAiAutomationWorkflowApi = async (workflowInput) => {
  console.log(
    `AI Automation API - createAiAutomationWorkflowApi - input: ${JSON.stringify(
      workflowInput
    )}`
  );

  try {
    // TODO: Replace with actual GraphQL mutation when schema is available
    // const response = await aiAutomationClient.mutate({
    //   mutation: CREATE_AI_AUTOMATION_WORKFLOW_MUTATION,
    //   variables: { input: workflowInput },
    // });

    // For now, simulate successful creation
    const mockResponse = {
      id: `workflow-${Date.now()}`,
      ...workflowInput,
      status: "created",
      createdAt: new Date().toISOString(),
    };

    return mockResponse;
  } catch (error) {
    console.error(`Error creating AI automation workflow: ${error.message}`);
    throw error;
  }
};

/**
 * Update an AI automation workflow
 */
export const updateAiAutomationWorkflowApi = async (workflowInput) => {
  console.log(
    `AI Automation API - updateAiAutomationWorkflowApi - input: ${JSON.stringify(
      workflowInput
    )}`
  );

  try {
    // TODO: Replace with actual GraphQL mutation when schema is available
    // const response = await aiAutomationClient.mutate({
    //   mutation: UPDATE_AI_AUTOMATION_WORKFLOW_MUTATION,
    //   variables: { input: workflowInput },
    // });

    // For now, simulate successful update
    const mockResponse = {
      ...workflowInput,
      status: "updated",
      updatedAt: new Date().toISOString(),
    };

    return mockResponse;
  } catch (error) {
    console.error(`Error updating AI automation workflow: ${error.message}`);
    throw error;
  }
};

/**
 * Delete an AI automation workflow
 */
export const deleteAiAutomationWorkflowApi = async (workflowId, tenantId) => {
  console.log(
    `AI Automation API - deleteAiAutomationWorkflowApi - workflowId: ${workflowId}, tenantId: ${tenantId}`
  );

  try {
    // TODO: Replace with actual GraphQL mutation when schema is available
    // const response = await aiAutomationClient.mutate({
    //   mutation: DELETE_AI_AUTOMATION_WORKFLOW_MUTATION,
    //   variables: { input: { workflowId, tenantId } },
    // });

    // For now, simulate successful deletion
    const mockResponse = {
      id: workflowId,
      success: true,
      deletedAt: new Date().toISOString(),
    };

    return mockResponse;
  } catch (error) {
    console.error(`Error deleting AI automation workflow: ${error.message}`);
    throw error;
  }
};

/**
 * Get a single AI automation workflow
 */
export const getAiAutomationWorkflowApi = async (workflowId, tenantId) => {
  console.log(
    `AI Automation API - getAiAutomationWorkflowApi - workflowId: ${workflowId}, tenantId: ${tenantId}`
  );

  try {
    // TODO: Replace with actual GraphQL query when schema is available
    // const response = await aiAutomationClient.query({
    //   query: GET_AI_AUTOMATION_WORKFLOW_QUERY,
    //   variables: { workflowId, tenantId },
    // });

    // For now, simulate workflow data
    const mockResponse = {
      id: workflowId,
      name: `AI Workflow ${workflowId}`,
      description: "AI-powered automation workflow",
      status: "active",
      tenantId,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    return mockResponse;
  } catch (error) {
    console.error(`Error getting AI automation workflow: ${error.message}`);
    throw error;
  }
};

/**
 * List AI automation workflows
 */
export const listAiAutomationWorkflowsApi = async (tenantId, status, limit) => {
  console.log(
    `AI Automation API - listAiAutomationWorkflowsApi - tenantId: ${tenantId}, status: ${status}, limit: ${limit}`
  );

  try {
    // TODO: Replace with actual GraphQL query when schema is available
    // const response = await aiAutomationClient.query({
    //   query: LIST_AI_AUTOMATION_WORKFLOWS_QUERY,
    //   variables: { tenantId, status, limit },
    // });

    // For now, simulate workflow list
    const mockWorkflows = [
      {
        id: "workflow-1",
        name: "Lead Qualification AI Workflow",
        description: "AI-powered lead qualification and scoring process",
        status: "active",
        category: "sales",
        tenantId,
        createdAt: new Date().toISOString(),
      },
      {
        id: "workflow-2",
        name: "Customer Onboarding AI Workflow",
        description: "AI-driven customer onboarding automation",
        status: "active",
        category: "onboarding",
        tenantId,
        createdAt: new Date().toISOString(),
      },
      {
        id: "workflow-3",
        name: "Follow-up Sequence AI Workflow",
        description: "Intelligent follow-up email sequence automation",
        status: "active",
        category: "marketing",
        tenantId,
        createdAt: new Date().toISOString(),
      },
      {
        id: "workflow-4",
        name: "Data Processing AI Workflow",
        description: "AI-powered data processing and analysis",
        status: "active",
        category: "analytics",
        tenantId,
        createdAt: new Date().toISOString(),
      },
    ];

    return mockWorkflows;
  } catch (error) {
    console.error(`Error listing AI automation workflows: ${error.message}`);
    throw error;
  }
};

/**
 * Execute an AI automation workflow
 */
// GraphQL mutation for creating AI automation
const CREATE_AI_AUTOMATION_MUTATION = gql`
  mutation CreateAiAutomation($input: CreateAiAutomationInput!) {
    createAiAutomation(input: $input) {
      success
      ticketId
      messageId
      error
    }
  }
`;

export const executeAiAutomationWorkflowApi = async (workflowInput) => {
  console.log(
    `AI Automation API - executeAiAutomationWorkflowApi - input: ${JSON.stringify(
      workflowInput
    )}`
  );

  try {
    // Use the correct createAiAutomation mutation from the schema
    console.log("Making actual API call to AI Automation endpoint...");

    // Transform the workflow input to match the CreateAiAutomationInput schema
    const automationInput = {
      automationType: workflowInput.automationType || "workflow-execution",
      description: `Execute workflow: ${workflowInput.workflowName}`,
      parameters: JSON.stringify({
        workflowId: workflowInput.workflowId,
        workflowName: workflowInput.workflowName,
        tenantId: workflowInput.tenantId,
        userMode: workflowInput.userMode || "default",
        cloudTarget: workflowInput.cloudTarget || "aws",
      }),
      priority: "normal",
      userMode: workflowInput.userMode || "default",
      source:
        workflowInput.executionContext?.source || "sales-process-dashboard",
      sourceScreen:
        workflowInput.executionContext?.sourceScreen || "workflow-builder",
      metadata: JSON.stringify({
        workflowId: workflowInput.workflowId,
        workflowName: workflowInput.workflowName,
        tenantId: workflowInput.tenantId,
        cloudTarget: workflowInput.cloudTarget || "aws",
        executionContext: workflowInput.executionContext,
        timestamp: workflowInput.timestamp,
      }),
    };

    console.log("Automation input:", automationInput);
    console.log("Apollo Client config:", {
      uri: AI_AUTOMATION_GRAPHQL_ENDPOINT,
      headers: {
        "x-api-key": "da2-z6wlsqhz4jeqbak2uzslvcz5ey",
        "Content-Type": "application/json",
      },
    });

    const response = await aiAutomationClient.mutate({
      mutation: CREATE_AI_AUTOMATION_MUTATION,
      variables: {
        input: automationInput,
      },
    });

    console.log("AI Automation API response:", response);

    if (response.data?.createAiAutomation?.success) {
      return {
        id:
          response.data.createAiAutomation.ticketId ||
          `execution-${Date.now()}`,
        workflowId: workflowInput.workflowId || "unknown",
        status: "executing",
        startedAt: new Date().toISOString(),
        result: {
          message: "AI automation workflow execution initiated successfully",
          executionId:
            response.data.createAiAutomation.ticketId || `exec-${Date.now()}`,
          ticketId: response.data.createAiAutomation.ticketId,
          messageId: response.data.createAiAutomation.messageId,
        },
      };
    } else {
      throw new Error(
        response.data?.createAiAutomation?.error ||
          "Failed to create AI automation ticket"
      );
    }
  } catch (error) {
    console.error(`Error executing AI automation workflow: ${error.message}`);
    console.error("Full error:", error);

    // Additional debugging for network errors
    if (error.networkError) {
      console.error("Network Error Details:", {
        message: error.networkError.message,
        statusCode: error.networkError.statusCode,
        bodyText: error.networkError.bodyText,
        result: error.networkError.result,
      });
    }

    if (error.graphQLErrors) {
      console.error("GraphQL Errors:", error.graphQLErrors);
    }

    throw error;
  }
};
